import { MutableRefObject, useCallback } from "react";
import { useSharedValue } from "react-native-reanimated";
import { GestureComposer, KeyLookupRef, MainGesturePanListener, PositionLookupRef } from "../types";
import useImageGalleryCarouselGesture from "./useImageGalleryCarouselGesture";
import useImageGalleryMainGesture from "./useImageGalleryMainGesture";

const useImageGalleryGestures = ({
	carouselItemDimensions,
	mainItemDimensions,
	positionLookup,
	curKey,
	setNewKey,
	keyLookup,
	mainGestureComposer,
	carouselGestureComposer,
	mainGesturePanListener,
}: {
	carouselItemDimensions: {
		width: number;
		height: number;
	};
	mainItemDimensions: {
		width: number;
		height: number;
	};
	positionLookup: PositionLookupRef;
	curKey: MutableRefObject<string>;
	setNewKey: (newKey: string) => void;
	keyLookup: KeyLookupRef;
	mainGestureComposer: GestureComposer | undefined;
	carouselGestureComposer: GestureComposer | undefined;
	mainGesturePanListener?: MainGesturePanListener;
}) => {
	const isDragging = useSharedValue(false);

	const processToPx = useCallback(
		(
			toPx: number,
		): {
			newPx: number;
		} => {
			const cur = positionLookup.current[curKey.current];
			if (!cur) throw new Error("cur is undefined");
			const curPx = cur.positionX;
			const newKey = keyLookup.current[toPx];
			const newLookupValue = newKey ? positionLookup.current[newKey] : undefined;
			const newPx = newLookupValue ? newLookupValue.positionX : curPx;
			if (newKey) {
				curKey.current = newKey;
				setNewKey(newKey);
			}
			return {
				newPx,
			};
		},
		[curKey, keyLookup, positionLookup, setNewKey],
	);

	const {
		gesture: mainGesture,
		containerStyle: mainContainerStyle,
		translateX: mainTranslateX,
		handlePage,
	} = useImageGalleryMainGesture({
		curKey,
		mainItemDimensions,
		positionLookup,
		isDragging,
		gestureComposer: mainGestureComposer,
		processToPx,
		mainGesturePanListener,
	});

	const { gesture: carouselGesture, containerStyle: carouselContainerStyle } =
		useImageGalleryCarouselGesture({
			mainTranslateX,
			carouselItemDimensions,
			mainItemDimensions,
			isDragging,
			gestureComposer: carouselGestureComposer,
			processToPx,
		});
	return {
		mainGesture,
		mainContainerStyle,
		carouselGesture,
		carouselContainerStyle,
		handlePage,
	};
};
export default useImageGalleryGestures;
